import { getJWTToken } from '../../utils/auth';
import { authHeaders, authHeadersFor } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type ImpersonationCandidate = {
  sub: string;
  /** The stable identity behind the sub, when the list carries one. */
  userId?: string;
  email: string;
  name: string;
  /** Empty once the person no longer belongs to a district. */
  districtId: string;
  districtName: string | null;
  role: string;
};

export type ImpersonationCandidateList = {
  available: boolean;
  users: ImpersonationCandidate[];
  truncated?: boolean;
};

export type ImpersonationSearch = {
  query: string;
  role: string;
};

export const IMPERSONATION_PAGE_SIZE = 100;

type RawCandidate = Partial<ImpersonationCandidate> | null;

type RawCandidateList = {
  available?: boolean;
  users?: RawCandidate[] | null;
  truncated?: boolean;
} | null;

function candidatesUrl(search: ImpersonationSearch | undefined): string {
  const base = `${apiUrl}/admin/impersonation/users`;
  if (!search) return base;
  let url = `${base}?limit=${IMPERSONATION_PAGE_SIZE}`;
  const query = search.query.trim();
  if (query) url += `&q=${encodeURIComponent(query)}`;
  if (search.role) url += `&role=${encodeURIComponent(search.role)}`;
  return url;
}

export async function fetchImpersonationCandidates(
  search?: ImpersonationSearch,
): Promise<ImpersonationCandidateList> {
  const token = await getJWTToken();
  const response = await fetch(candidatesUrl(search), {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`impersonation users failed: ${response.status}`);
  }
  const data = ((await response.json()) as RawCandidateList) ?? {};
  const users = (data.users ?? []).flatMap((row) =>
    row && typeof row.sub === 'string' && row.sub && typeof row.email === 'string'
      ? [
          {
            sub: row.sub,
            userId: typeof row.userId === 'string' && row.userId !== '' ? row.userId : undefined,
            email: row.email,
            name: row.name ?? '',
            districtId: typeof row.districtId === 'string' ? row.districtId : '',
            districtName: row.districtName ?? null,
            role: row.role ?? '',
          },
        ]
      : [],
  );
  return {
    available: data.available === true,
    users,
    truncated: data.truncated === true,
  };
}

const ROLE_LABELS = new Map<string, string>([
  ['district_admin', 'Admin'],
  ['teacher', 'Teacher'],
]);

export function roleLabel(role: string): string {
  return ROLE_LABELS.get(role) ?? role;
}

export const ROLE_OPTIONS = Array.from(ROLE_LABELS.keys());

export function filterCandidates(
  rows: ImpersonationCandidate[],
  query: string,
  role = '',
): ImpersonationCandidate[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const byRole = role ? rows.filter((row) => row.role === role) : rows;
  if (words.length === 0) return byRole;
  return byRole.filter((row) => {
    const haystack = `${row.email} ${row.name} ${row.districtName ?? ''}`.toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
}

export type ImpersonationLookupReason = 'super_admin' | 'no_access' | 'unresolved';

export type ImpersonationLookup = {
  found: boolean;
  eligible: boolean;
  reason: ImpersonationLookupReason | null;
  sub: string | null;
  email: string | null;
  districtName: string | null;
  districtState: string | null;
};

type RawLookup = Partial<ImpersonationLookup> | null;

export async function lookupImpersonationTarget(
  email: string,
  { asActor = false, token: givenToken }: { asActor?: boolean; token?: string } = {},
): Promise<ImpersonationLookup> {
  const token = givenToken ?? (await getJWTToken());
  const query = encodeURIComponent(email.trim().toLowerCase());
  const response = await fetch(`${apiUrl}/admin/impersonation/lookup?email=${query}`, {
    headers: asActor ? authHeadersFor(token, null) : authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`impersonation lookup failed: ${response.status}`);
  }
  const data = ((await response.json()) as RawLookup) ?? {};
  const eligible = data.eligible === true;
  return {
    found: data.found === true,
    eligible,
    reason: data.reason ?? null,
    sub: eligible ? (data.sub ?? null) : null,
    email: eligible ? (data.email ?? null) : null,
    districtName: eligible ? (data.districtName ?? null) : null,
    districtState: eligible ? (data.districtState ?? null) : null,
  };
}
